import { useEffect, useMemo, useState, type ReactElement, type ReactNode } from 'react';
import { loadedMarkdownRenderer, loadMarkdownRenderer } from './markdown-renderer';
import styles from './Markdown.module.scss';

export interface MarkdownProps {
  /** Текст в Markdown. */
  text: string;
  /** Что показать вместо пустого текста. */
  empty?: ReactNode;
}

/**
 * Текст в Markdown (ADR-016): до загрузки разборщика — исходный текст как есть, потом — разметка.
 * HTML из текста выводится текстом, опасные ссылки ссылками не становятся.
 */
export function Markdown({ text, empty }: MarkdownProps): ReactElement | null {
  const [ready, setReady] = useState(() => loadedMarkdownRenderer() !== null);
  const blank = text.trim() === '';

  useEffect(() => {
    if (ready || blank) return;
    let alive = true;
    loadMarkdownRenderer()
      .then(() => {
        if (alive) setReady(true);
      })
      .catch(() => {
        // Остаётся исходный текст — он читается и без разметки.
      });
    return () => {
      alive = false;
    };
  }, [ready, blank]);

  const html = useMemo(() => (ready && !blank ? loadedMarkdownRenderer()!(text) : null), [ready, blank, text]);

  if (blank) return empty ? <p className={styles.empty}>{empty}</p> : null;
  if (html === null) return <div className={styles.plain}>{text}</div>;
  // Разметка micromark: HTML из текста экранирован, опасные ссылки убраны (markdown-renderer.ts).
  return <div className={styles.markdown} dangerouslySetInnerHTML={{ __html: html }} />;
}
